'use client'

import React from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import FeaturedCategoryArrowIcon from '@/components/Icons/FeaturedCategoryArrowIcon'
import type { ServiceItem } from '@/types/interfaces/service-item'
import { staggerItem } from '@/lib/animations'
import Background, { type BackgroundKey } from './Background'
import SubtitleLeading, { type SubtitleLeadingKey } from './SubtitleLeading/index'
import Services from './Services'
import classes from './Card.module.css'

export type Columns = 4 | 6 | 8
export type { BackgroundKey, SubtitleLeadingKey }

export interface CardProps {
    title: string
    subtitle?: string
    subtitleLeading?: SubtitleLeadingKey
    note?: string
    href?: string
    background?: BackgroundKey
    columns: Columns
    services: ServiceItem[]
    /** Eager-load this card's background when it is the first above-fold image. */
    priority?: boolean
}

export default function Card({
    title,
    subtitle,
    subtitleLeading,
    note,
    href,
    background,
    columns,
    services,
    priority = false,
}: CardProps) {
    const hasSubtitle = subtitle != null && subtitle !== ''
    const hasSubtitleLeading = subtitleLeading != null
    const hasServices = services.length > 0
    const hasHref = href != null && href !== ''

    const headerContent = (
        <>
            <div className={classes.primaryText}>
                <div className={classes.titleRow}>
                    <div className={classes.title} data-text-layer="title">
                        {title}
                    </div>
                    {hasHref && (
                        <span className={classes.hrefButton} aria-hidden>
                            <FeaturedCategoryArrowIcon />
                        </span>
                    )}
                </div>
                {(hasSubtitle || hasSubtitleLeading) && (
                    <div className={classes.subtitle} data-text-layer="subtitle">
                        {hasSubtitleLeading && <SubtitleLeading leading={subtitleLeading} />}
                        {hasSubtitle && <span className={classes.subtitleText}>{subtitle}</span>}
                    </div>
                )}
            </div>
            {note != null && note !== '' && (
                <div className={classes.note} data-text-layer="note">
                    {note}
                </div>
            )}
        </>
    )

    return (
        <motion.div
            className={classes.card}
            data-featured-category-title={title}
            data-featured-category-columns={columns}
            data-featured-category-background={background}
            style={
                {
                    '--featured-category-columns': columns,
                } as React.CSSProperties
            }
            variants={staggerItem}
        >
            {background != null && <Background background={background} priority={priority} />}
            <div className={classes.content}>
                {hasHref ? (
                    <Link
                        href={href}
                        className={`${classes.textArea} ${classes.headerLink}`}
                        aria-label={title}
                        prefetch={false}
                    >
                        {headerContent}
                    </Link>
                ) : (
                    <div className={classes.textArea}>{headerContent}</div>
                )}
                {hasServices && <Services services={services} />}
            </div>
        </motion.div>
    )
}
